<template>
  <div class="module_scheme_preview">
    <div class="module_scheme_preview__bar">
      <span class="module_scheme_preview__info">{{ info }}</span>
      <v-spacer></v-spacer>
      <template v-if="preview">
        <v-btn icon small title="Уменьшить" @click="zoomOut">
          <v-icon>mdi-magnify-minus-outline</v-icon>
        </v-btn>
        <v-btn icon small title="Увеличить" @click="zoomIn">
          <v-icon>mdi-magnify-plus-outline</v-icon>
        </v-btn>
        <v-btn small text :disabled="zoom === null" @click="zoom = null">Вписать</v-btn>
      </template>
    </div>

    <div v-if="loading" class="module_scheme_preview__state">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
    <div v-else-if="error" class="module_scheme_preview__state">{{ error }}</div>
    <template v-else-if="preview">
      <div
        v-for="problem in preview.problems"
        :key="problem"
        class="module_scheme_preview__problem"
      >
        {{ problem }}
      </div>
      <div class="module_scheme_preview__scroll">
        <svg
          :viewBox="viewBox"
          :style="svgStyle"
          class="module_scheme_preview__svg"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            :x="preview.viewBox.x"
            :y="preview.viewBox.y"
            :width="preview.viewBox.w"
            :height="preview.viewBox.h"
            fill="white"
          />
          <!-- трубы на стыках лежат под модулями, как на холсте конструктора -->
          <path
            v-for="(seam, i) in preview.seams"
            :key="`seam-${i}`"
            :d="seam.d"
            :stroke="seam.color"
            :stroke-width="seam.kind === 'orphan' ? 1.6 : 2"
            :stroke-dasharray="
              seam.kind === 'offset' ? '6 4' : seam.kind === 'conflict' ? '3 3' : undefined
            "
            fill="none"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <g v-for="m in preview.modules" :key="m.uid" :transform="`translate(${m.x},${m.y})`">
            <image v-if="m.url" :href="m.url" x="0" y="0" :width="m.w" :height="m.h" />
            <!-- Элементы оборудования: рамка под мышью и выбранные для массового назначения — фиолетовая
                 (активный цвет), другие экземпляры того же типа во всех модулях — золотая; с позицией — зелёная, куплено —
                 оранжевая, установлено — бирюзовая, остальные — пунктир. Клик открывает подбор,
                 Shift/Ctrl/Cmd+клик добавляет слот к выбору. Координаты — в viewBox узла, картинка
                 рисуется с (0, 0), поэтому вычитаем начало viewBox. -->
            <g v-for="slot in slotsOf(m.uid)" :key="`slot-${slot.key}`" style="cursor: pointer">
              <!-- подложка-«маркер»: заливка поверх картинки в режиме multiply — линии чертежа остаются
                   чёрными, а фон элемента закрашивается (картинка модуля — один <image>, внутри неё
                   ничего не перекрасить, поэтому подсвечиваем сверху) -->
              <rect
                v-if="slotWash(m.uid, slot)"
                :x="slot.box.x - m.origin.x - 1"
                :y="slot.box.y - m.origin.y - 1"
                :width="slot.box.w + 2"
                :height="slot.box.h + 2"
                :fill="slotWash(m.uid, slot)"
                style="mix-blend-mode: multiply"
                pointer-events="none"
              />
              <rect
                :x="slot.box.x - m.origin.x - 1"
                :y="slot.box.y - m.origin.y - 1"
                :width="slot.box.w + 2"
                :height="slot.box.h + 2"
                fill="transparent"
                @mouseenter="$emit('hover-slot', { uid: m.uid, key: slot.key, fromSvg: true })"
                @mouseleave="$emit('hover-slot', null)"
                @click="
                  $emit('pick-slot', {
                    uid: m.uid,
                    key: slot.key,
                    multi: $event.shiftKey || $event.metaKey || $event.ctrlKey,
                  })
                "
              >
                <title>{{ slotTitle(slot) }}</title>
              </rect>
              <rect
                :x="slot.box.x - m.origin.x - 1"
                :y="slot.box.y - m.origin.y - 1"
                :width="slot.box.w + 2"
                :height="slot.box.h + 2"
                fill="none"
                :stroke="slotStroke(m.uid, slot)"
                :stroke-width="
                  isSlotHot(m.uid, slot) || isSlotSibling(m.uid, slot) || isFunctionalityRelated(slot) || slot.selected ? 2 : slot.frozen ? 1.5 : 1
                "
                :stroke-dasharray="isSlotHot(m.uid, slot) || isFunctionalityRelated(slot) || slot.chosen || slot.selected ? null : '1.5 1.5'"
                vector-effect="non-scaling-stroke"
                pointer-events="none"
              />
            </g>
            <rect
              :width="m.w"
              :height="m.h"
              fill="none"
              stroke="#9a9290"
              stroke-width="1"
              stroke-dasharray="3 3"
              vector-effect="non-scaling-stroke"
            />
            <text x="0" y="-8" font-size="11" fill="#555">{{ m.label }}</text>
          </g>
        </svg>
      </div>
      <div v-if="preview.legend.length" class="module_scheme_preview__legend">
        <span
          v-for="item in preview.legend"
          :key="item.code"
          class="module_scheme_preview__legend_item"
        >
          <i :style="{ background: item.color }"></i>{{ item.name }}
        </span>
      </div>
      <div v-if="functionalityItems.length" class="module_scheme_preview__functionalities">
        <div class="module_scheme_preview__functionalities_title">Функциональность элементов</div>
        <button
          v-for="item in functionalityItems"
          :key="`${item.idNode}-${item.id}-${item.moduleTitle}`"
          type="button"
          class="module_scheme_preview__functionality"
          :class="{ 'module_scheme_preview__functionality--active': isFunctionalityHovered(item) }"
          @mouseenter="setHoveredFunctionality(item)"
          @mouseleave="setHoveredFunctionality(null)"
        >
          <span class="module_scheme_preview__functionality_module">{{ item.moduleTitle }}</span>
          <template v-for="(part, index) in functionalityTextParts(item.text)">
            <span
              v-if="part.type === 'element'"
              :key="`functionality-part-${index}`"
              class="module_scheme_preview__functionality_link"
            >{{ part.text }}</span>
            <span v-else :key="`functionality-part-${index}`">{{ part.text }}</span>
          </template>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import { ACTIVE_COLOR, SLOT_STATUS } from "@/helpers/moduleSchemeSlots";
import { functionalityTextParts, linkedElementRefs } from "@/helpers/schemeNodeFunctionalities";

// Активный цвет: слот под курсором и выделенные для массового назначения — фиолетовый; другие
// экземпляры того же типа — тёмное золото (контраст к белому 3.9:1; чистый жёлтый и янтарный — меньше 2:1)
const CURRENT_COLOR = ACTIVE_COLOR;
const SIBLING_COLOR = "#A87900";
// Заливка-подложка под рамкой (режим multiply: линии чертежа не светлеют). Под курсором плотнее, чем у
// выделенных, — чтобы слот под мышью было видно и среди выбранных
const WASH_CURRENT = "rgba(123,31,162,0.24)";
const WASH_SELECTED = "rgba(123,31,162,0.14)";
const WASH_SIBLING = "rgba(255,214,0,0.55)";
const FUNCTIONALITY_COLOR = "#00838F";
const WASH_FUNCTIONALITY = "rgba(0,150,136,0.28)";
const ZOOM_STEP = 0.25;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 4;

/**
 * Предпросмотр модульной схемы: SVG модулей на своих местах и трубы на стыках.
 * Геометрию считает бэк (GET module-schemes/{id}/seams), компонент её только рисует
 * и накладывает рамки слотов. Встроен в окно редактирования схемы: грузится,
 * когда приходит schemeId, и сбрасывается, когда он снова null (окно закрыли).
 */
export default {
  name: "ModuleSchemePreview",
  props: {
    schemeId: { type: Number, default: null },
    // слоты оборудования по модулям (из ModuleSchemeSlots) — по ним рисуем рамки на элементах
    slotGroups: { type: Array, default: () => [] },
    // слот под курсором — на схеме или в списке: { uid, key, fromSvg } или null
    hoveredSlot: { type: Object, default: null },
  },
  data: () => ({
    loading: false,
    error: "",
    preview: null,
    // null — схема целиком в окне; число — масштаб в пикселях на единицу чертежа
    zoom: null,
    // ответ запроса устаревает, если за время загрузки открыли другую схему
    requestId: 0,
    hoveredFunctionality: null,
  }),
  computed: {
    viewBox() {
      const vb = this.preview.viewBox;
      return `${vb.x} ${vb.y} ${vb.w} ${vb.h}`;
    },
    svgStyle() {
      if (this.zoom === null) return { width: "100%", height: "100%" };
      const vb = this.preview.viewBox;
      return { width: `${vb.w * this.zoom}px`, height: `${vb.h * this.zoom}px` };
    },
    // слоты по uid модуля — чтобы не искать их в каждой отрисовке
    slotsByUid() {
      const map = {};
      for (const g of this.slotGroups) map[g.uid] = g.slots;
      return map;
    },
    info() {
      if (!this.preview) return "";
      return `Модулей: ${this.preview.modules.length} · стыков: ${this.preview.links}`;
    },
    functionalityItems() {
      return this.slotGroups.flatMap((group) =>
        (group.functionalities || []).map((functionality) => ({
          ...functionality,
          idNode: group.idNode,
          moduleTitle: group.title,
        }))
      );
    },
  },
  watch: {
    schemeId: {
      immediate: true,
      handler(id) {
        if (id) this.load(id);
        else this.reset();
      },
    },
  },
  methods: {
    functionalityTextParts,
    reset() {
      this.requestId++;
      this.loading = false;
      this.error = "";
      this.preview = null;
      this.zoom = null;
      this.hoveredFunctionality = null;
    },
    async load(id) {
      this.reset();
      const requestId = this.requestId;
      this.loading = true;

      const data = await this.$store.dispatch("ModuleSchemesModule/loadSeams", id);
      if (requestId !== this.requestId) return;
      this.loading = false;

      if (!data) {
        this.error = "Не удалось загрузить схему";
        return;
      }
      if (!data.modules.length) {
        this.error = data.problems.length ? data.problems.join(". ") : "В схеме ещё нет модулей";
        return;
      }
      this.preview = data;
    },
    slotsOf(uid) {
      return this.slotsByUid[uid] || [];
    },
    slotTitle(slot) {
      const what = slot.chosen
        ? `${slot.chosen.name || "…"}${slot.quantity > 1 ? ` × ${slot.quantity}` : ""}`
        : "выбрать номенклатуру";
      const status = slot.frozen ? ` (${SLOT_STATUS[slot.status].label.toLowerCase()})` : "";
      return `${slot.label} — ${what}${status}${slot.frozen ? "" : " · Shift+клик — добавить к выбору"}`;
    },
    // под курсором именно этот слот
    isSlotHot(uid, slot) {
      const h = this.hoveredSlot;
      return !!h && h.uid === uid && h.key === slot.key;
    },
    isFunctionalityRelated(slot) {
      const hovered = this.hoveredFunctionality;
      return !!(
        hovered &&
        slot.idNode === hovered.idNode &&
        hovered.elementRefs.some(
          (ref) => Number(ref.id) === Number(slot.idElement) && (ref.instance == null || Number(ref.instance) === Number(slot.n))
        )
      );
    },
    isFunctionalityHovered(item) {
      return !!(
        this.hoveredFunctionality &&
        this.hoveredFunctionality.idNode === item.idNode &&
        this.hoveredFunctionality.id === item.id
      );
    },
    setHoveredFunctionality(item) {
      if (!item) {
        this.hoveredFunctionality = null;
        return;
      }
      const textRefs = linkedElementRefs(item.text || "");
      const elementRefs = textRefs.length
        ? textRefs
        : item.element_refs && item.element_refs.length
          ? item.element_refs
          : (item.element_ids || []).map((id) => ({ id: Number(id), instance: null }));
      this.hoveredFunctionality = { id: item.id, idNode: item.idNode, elementRefs };
    },
    // другой экземпляр того же типа — в любом модуле схемы (при наведении на КШ-1 это КШ-2, КШ-3… этого
    // модуля и все КШ остальных модулей): показываем, где ещё стоит такое же оборудование
    isSlotSibling(uid, slot) {
      const h = this.hoveredSlot;
      if (!h || (h.uid === uid && h.key === slot.key)) return false;
      return Number(h.key.split(":")[0]) === slot.idElement;
    },
    slotStroke(uid, slot) {
      if (this.isSlotHot(uid, slot) || slot.selected) return CURRENT_COLOR;
      if (this.isFunctionalityRelated(slot)) return FUNCTIONALITY_COLOR;
      if (this.isSlotSibling(uid, slot)) return SIBLING_COLOR;
      if (slot.frozen) return SLOT_STATUS[slot.status].stroke;
      return slot.chosen ? "#2e9e5b" : "#9e9e9e";
    },
    // заливка-подложка под рамкой: текущий — фиолетовая, выделенный — синяя, остальные экземпляры того же
    // типа — жёлтая «маркерная» (рамка у них тёмное золото, а на мелкой схеме одной рамки мало)
    slotWash(uid, slot) {
      if (this.isSlotHot(uid, slot)) return WASH_CURRENT;
      if (slot.selected) return WASH_SELECTED;
      if (this.isFunctionalityRelated(slot)) return WASH_FUNCTIONALITY;
      if (this.isSlotSibling(uid, slot)) return WASH_SIBLING;
      return null;
    },
    zoomIn() {
      this.zoom = this.zoom === null ? 1.5 : Math.min(ZOOM_MAX, this.zoom + ZOOM_STEP);
    },
    zoomOut() {
      this.zoom = this.zoom === null ? 1 : Math.max(ZOOM_MIN, this.zoom - ZOOM_STEP);
    },
  },
};
</script>

<style lang="scss" scoped>
.module_scheme_preview {
  display: flex;
  flex-direction: column;
  min-width: 0;
  &__bar {
    display: flex;
    align-items: center;
    min-height: 36px;
    margin-bottom: 8px;
    font-size: 0.85em;
    color: rgba(0, 0, 0, 0.6);
  }
  &__state {
    display: flex;
    justify-content: center;
    padding: 48px 0;
    color: rgba(0, 0, 0, 0.6);
  }
  &__problem {
    margin-bottom: 8px;
    color: #bf0022;
    font-size: 0.85em;
  }
  &__scroll {
    overflow: auto;
    // схема целиком занимает окно; выше — только то, что остаётся от высоты экрана
    // после заголовка, кнопок и легенды
    height: calc(90vh - 330px);
    min-height: 360px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    background: #fff;
  }
  &__svg {
    display: block;
    font-family: sans-serif;
  }
  &__legend {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 16px;
    margin-top: 12px;
    font-size: 0.8em;
    color: rgba(0, 0, 0, 0.7);
  }
  &__legend_item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    i {
      width: 14px;
      height: 4px;
      border-radius: 2px;
      display: inline-block;
    }
  }
  &__functionalities {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding: 8px 0 2px;
  }
  &__functionalities_title {
    width: 100%;
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.8em;
    font-weight: 600;
  }
  &__functionality {
    padding: 4px 8px;
    color: #37474f;
    background: #eceff1;
    border: 1px solid #cfd8dc;
    border-radius: 4px;
    cursor: pointer;
    font: inherit;
    font-size: 0.82em;
    text-align: left;
    &:hover,
    &--active {
      color: #006064;
      background: #e0f2f1;
      border-color: #00838f;
    }
  }
  &__functionality_module {
    margin-right: 5px;
    color: #607d8b;
    font-weight: 600;
  }
  &__functionality_link {
    color: #006064;
    text-decoration: underline;
    text-decoration-style: dotted;
  }
}
</style>
